import { fromUint8Array } from 'js-base64'
import { gzip } from 'pako'

import { toolTuningFields } from '../ccm'
import { kqlEscape } from '../escape-utils'

import { Tool } from './base'
import {
  makeRenderer,
  PipeAndGroups,
  RenderContext,
  normalizeList,
  scalarToString,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import {
  joinWithPipeBlock,
  wrapRendererWithDatasource,
} from './render/rtd/datasource.utils'
import { defenderMap } from './render/rtd/tool-datasource-mappings/microsoft-defender.datasource'
import { AstPredicate, ValueOrList } from './render/types'

// KQL DataDisplay template — shared by Microsoft Defender (bare field names, no Eta wrapping)
export const kqlDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields) => `| project ${fields.join(', ')}`,

  groupBy: (groupFields, aggClauses) => {
    const allClauses = [
      'firstEvent=min(Timestamp)',
      'lastEvent=max(Timestamp)',
      ...aggClauses,
      'Count=count()',
    ]
    return `| summarize ${allClauses.join(', ')} by ${groupFields.join(', ')}\n| order by firstEvent asc`
  },

  timeBucket: (span, groupFields, aggClauses) => {
    const allClauses = [
      'firstEvent=min(Timestamp)',
      'lastEvent=max(Timestamp)',
      ...aggClauses,
      'Count=count()',
    ]
    const byFields = [...groupFields, `bin(Timestamp, ${span})`].join(', ')
    return `| summarize ${allClauses.join(', ')} by ${byFields}\n| order by firstEvent asc`
  },

  aggFns: {
    count: (_field, alias) => `${alias}=count()`,
    dcount: (field, alias) => `${alias}=dcount(${field})`,
    values: (field, alias) => `${alias}=make_set(${field})`,
    collect: (field, alias) => `${alias}=make_set(${field})`,
    first: (field, alias) => `${alias}=arg_min(Timestamp, ${field || '*'})`,
    last: (field, alias) => `${alias}=arg_max(Timestamp, ${field || '*'})`,
    sum: (field, alias) => `${alias}=sum(${field})`,
    avg: (field, alias) => `${alias}=avg(${field})`,
    min: (field, alias) => `${alias}=min(${field})`,
    max: (field, alias) => `${alias}=max(${field})`,
  },

  fieldAlias: (field, alias) => `${alias}=${field}`,

  join: (body, ddClause) => `${body}\n${ddClause}`,
}

export { defenderMap }

export class MicrosoftDefenderTool extends Tool {
  public readonly toolName = 'Microsoft Defender'
  public readonly toolKind = 'edr' as const
  public override readonly datasourceMap = defenderMap
  public readonly dataDisplayTemplate = kqlDataDisplayTemplate

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = fromUint8Array(
      gzip(renderedContent.split('').join('\x00') + '\x00'),
      true,
    )
    return `${rootURL}v2/advanced-hunting?query=${encodedRenderedContent}&timeRangeId=day`
  }

  public pivotEscape(key: string, value: string): string {
    return kqlEscape(value)
  }

  /**
   * Microsoft Defender for Endpoint (KQL) renderer
   *
   * Layout & grouping
   * - Uses PipeAndGroups (root AND → one line per child; nested groups inline).
   * - linePrefix: "| where " for each root-level clause.
   * - and/or: lowercase (" and ", " or ").
   *
   * Operators
   * - Equality (case-insensitive): single → `=~`, list → `in~ (...)`
   * - Inequality: single → `!~`, list → `!in~ (...)`
   * - Contains family: map to token-aware ops (faster index usage):
   *     contains        → has
   *     doesnotcontain  → !has
   *     multi-values: has_any / !has_any
   * - Starts/Ends: `startswith`, `endswith`
   * - Comparisons: `<`, `<=`, `>`, `>=`
   * - Null/Empty: `isnull(field)`, `isnotnull(field)`, `isempty(field)`, `isnotempty(field)`
   *
   * Quoting
   * - **Numbers are not quoted.** Strings are double-quoted with escaping.
   *
   * Field mapping
   * - Uses toolTuningFields["microsoft_defender"] via RenderContext.mapField.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: PipeAndGroups,
    renderContext: {
      linePrefix: '| where ',
      andGlue: ' and ',
      orGlue: ' or ',
      wrapField: (f) => f, // no Eta wrapping for Defender
      // quote(): KQL string literal with escaping
      quote: (s: string) =>
        `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
      // list() not used directly; we build per-item with smart quoting in LIST() above
    },
    formatPredicate(p: AstPredicate, renderContext: RenderContext): string {
      const f = renderContext.wrapField(renderContext.mapField(p.field))

      // Raw list preserves original types so we can avoid quoting numbers
      const raw: ValueOrList | undefined = p.value
      const rawList =
        raw == null ? [undefined] : Array.isArray(raw) ? raw : [raw]

      // String list (for shaping/escaping where needed)
      const vals = normalizeList(p.value).map(scalarToString)

      // Quote smartly: numbers → bare; everything else → renderContext.quote
      const Q = (s: string, rawVal: any) =>
        typeof rawVal === 'number' ? String(rawVal) : renderContext.quote(s)

      const LIST = () => {
        const items = vals.map((s, i) => Q(s, rawList[i]))
        return `(${items.join(', ')})`
      }

      // Helpers
      const isMulti = vals.length > 1

      switch (p.op) {
        // Null / Empty (KQL built-ins)
        case 'isnull':
          return `isnull(${f})`
        case 'isnotnull':
          return `isnotnull(${f})`
        case 'isempty':
          return `isempty(${f})`
        case 'isnotempty':
          return `isnotempty(${f})`

        // Equality / inequality (prefer case-insensitive =~ / in~)
        case 'eq':
          return isMulti
            ? `${f} in~ ${LIST()}`
            : `${f} =~ ${Q(vals[0], rawList[0])}`
        case 'neq':
          return isMulti
            ? `${f} !in~ ${LIST()}`
            : `${f} !~ ${Q(vals[0], rawList[0])}`

        // Token-oriented contains (faster than substring)
        case 'contains':
          return isMulti
            ? `${f} has_any ${LIST()}`
            : `${f} has ${Q(vals[0], rawList[0])}`
        case 'doesnotcontain':
          return isMulti
            ? `not(${f} has_any ${LIST()})`
            : `${f} !has ${Q(vals[0], rawList[0])}`
        // Starts with and ends with
        case 'startswith':
          return isMulti
            ? `(${vals.map((s, i) => `${f} startswith ${Q(s, rawList[i])}`).join(renderContext.orGlue)})`
            : `${f} startswith ${Q(vals[0], rawList[0])}`
        case 'endswith':
          return isMulti
            ? `(${vals.map((s, i) => `${f} endswith ${Q(s, rawList[i])}`).join(renderContext.orGlue)})`
            : `${f} endswith ${Q(vals[0], rawList[0])}`

        // Comparisons (use smart quoting)
        case 'lt':
          return `${f} < ${Q(vals[0], rawList[0])}`
        case 'lte':
          return `${f} <= ${Q(vals[0], rawList[0])}`
        case 'gt':
          return `${f} > ${Q(vals[0], rawList[0])}`
        case 'gte':
          return `${f} >= ${Q(vals[0], rawList[0])}`

        default:
          // Safe default → equality
          return isMulti
            ? `${f} in~ ${LIST()}`
            : `${f} =~ ${Q(vals[0], rawList[0])}`
      }
    },
  })

  public readonly tuningText =
    "Insert tuning logic after the last line of base logic (ex. | where FileName has \"example\"). This will typically be right before the '| project ...' or '| summarize ...' functions."

  /** Emit the KQL table name + optional event-type filter from the resolved datasource. */
  public emitMicrosoftDefenderDatasource(
    ds: NormalizedDataSource | undefined,
  ): string {
    if (!ds?.repo) return ''
    if (ds.events?.length) {
      const eventsField =
        toolTuningFields[this.toolName]?.['event_type'] ?? 'ActionType'
      const Q = (e: string) => `"${e}"`
      const filter =
        ds.events.length === 1
          ? `${eventsField} =~ ${Q(ds.events[0])}`
          : `${eventsField} in~ (${ds.events.map(Q).join(', ')})`
      return `${ds.repo}\n| where ${filter}`
    }
    return ds.repo
  }

  /** Merge the base logic (tuning) with datasource table routing and DataDisplay clause. */
  public renderMicrosoftDefender = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitMicrosoftDefenderDatasource(ds),
      joinWithPipeBlock,
      this.tuningText,
    ),
    this.toolName,
    kqlDataDisplayTemplate,
  )
}
